import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API, { fmt, apiError } from "@/lib/api";
import { PageHeader, StatCard, StatusPill, PackageBadge, Loader } from "@/components/ui-bits";
import {
  Wallet, PiggyBank, Crown, TrendingUp, Package, Users, Network,
  ArrowUpRight, ArrowDownToLine, Sparkles, Layers, Lock, Unlock, Loader2, Star,
} from "lucide-react";

function Countdown({ until }) {
  const [left, setLeft] = useState("");
  useEffect(() => {
    const tick = () => {
      const ms = new Date(until) - new Date();
      if (ms <= 0) return setLeft("expired");
      const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4), s = Math.floor((ms % 6e4) / 1000);
      setLeft(`${h}h ${m}m ${s}s`);
    };
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [until]);
  return <span className="font-num">{left}</span>;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [buyingCards, setBuyingCards] = useState(false);

  const loadDashboard = () => API.get("/dashboard").then((r) => setData(r.data)).catch((e) => {});
  useEffect(() => {
    loadDashboard();
    API.get("/silver-card/status").then((r) => setStatus(r.data)).catch((e) => {});
  }, []);

  const buyCards = async () => {
    setBuyingCards(true);
    try {
      const r = await API.post("/magic-cards/buy");
      toast.success(r.data.message);
      await loadDashboard();
    } catch (e) { toast.error(apiError(e)); } finally { setBuyingCards(false); }
  };

  if (!data) return <Loader label="Loading dashboard" />;

  const u = data.user;
  const pkg = data.package;
  const li = data.level_income;
  const capUsed = u.earnings_generated || 0;
  const cap = u.earning_cap || 0;
  const pct = cap > 0 ? Math.min(100, (capUsed / cap) * 100) : 0;

  return (
    <div>
      <PageHeader
        title={`Welcome, ${u.name.split(" ")[0]}`}
        subtitle="Your network wallet, package status and 6-level earnings at a glance."
        testid="dashboard-header"
        right={
          <div className="flex gap-2 items-center">
            {u.is_advance && (
              <span data-testid="advance-member-badge" className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500/20 to-yellow-500/10 text-amber-300 border border-amber-500/40">
                <Star className="h-4 w-4" /> Advance Member
              </span>
            )}
            <Link to="/app/packages" data-testid="quick-topup" className="text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-4 py-2.5 rounded-xl inline-flex items-center gap-2"><Package className="h-4 w-4" /> Join / Upgrade</Link>
            <Link to="/app/withdraw" data-testid="quick-withdraw" className="text-sm font-semibold border border-white/15 hover:border-emerald-500/40 text-white px-4 py-2.5 rounded-xl inline-flex items-center gap-2"><ArrowDownToLine className="h-4 w-4" /> Withdraw</Link>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <StatCard testid="stat-main-wallet" label="Main Wallet" value={u.main_wallet} money icon={Wallet} accent="emerald" />
        <StatCard testid="stat-topup-wallet" label="Top-Up Wallet" value={u.topup_wallet} money icon={PiggyBank} accent="cyan" />
        <StatCard testid="stat-magic-wallet" label="Magic Card Wallet" value={u.magic_card_wallet} money icon={Sparkles} accent="violet" />
        <StatCard testid="stat-deposit-wallet" label="Deposit Wallet (P/P)" value={u.deposit_wallet} money icon={ArrowUpRight} accent="violet" />
        <StatCard testid="stat-total-earning" label="Total Earnings" value={u.total_earning} money icon={TrendingUp} accent="gold" />
      </div>

      {status && (
        <div className={`glass rounded-2xl p-5 mb-8 flex items-center justify-between gap-4 ${status.main_active ? "ring-1 ring-emerald-500/40" : "ring-1 ring-rose-500/30"}`} data-testid="dashboard-main-status">
          <div className="flex items-center gap-3">
            <div className={`h-11 w-11 rounded-xl grid place-items-center ${status.main_active ? "bg-emerald-500/15" : "bg-rose-500/15"}`}>
              {status.main_active ? <Unlock className="h-5 w-5 text-emerald-400" /> : <Lock className="h-5 w-5 text-rose-400" />}
            </div>
            <div>
              <div className="text-[0.7rem] uppercase font-mono tracking-[0.18em] text-slate-400">Main Wallet</div>
              <div className={`font-display text-lg font-bold ${status.main_active ? "text-emerald-300" : "text-rose-300"}`} data-testid="dashboard-main-status-text">
                {status.main_active ? "Active — Unlocked" : "Locked"}
              </div>
              {status.main_active ? (
                <p className="text-xs text-slate-400">Move &amp; withdraw enabled for <Countdown until={status.main_active_until} /></p>
              ) : (
                <p className="text-xs text-slate-500">Drop a Silver Card to move funds out or withdraw. Funds can still flow IN while locked.</p>
              )}
            </div>
          </div>
          {!status.main_active && (
            <Link to="/app/silver" data-testid="dashboard-drop-link" className="shrink-0 text-sm font-semibold bg-amber-500 hover:bg-amber-400 text-slate-900 px-4 py-2.5 rounded-xl inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" /> Drop Silver Card
            </Link>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        {/* Package card */}
        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="package-card">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2"><Package className="h-5 w-5 text-emerald-400" /> Package & Earning Cap</h3>
            {pkg ? <PackageBadge id={pkg.id} /> : <StatusPill status="inactive" />}
          </div>
          {pkg ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                <Info label="Package" value={pkg.legacy ? `${pkg.name} (legacy)` : pkg.name} />
                <Info label="Amount" value={fmt(pkg.amount + (pkg.silver_card || 0))} />
                <Info label="Earning Cap" value={fmt(cap)} />
                <Info label="Remaining" value={fmt(data.remaining_cap)} />
              </div>
              <div className="mb-1 flex justify-between text-xs text-slate-400">
                <span>Cap utilisation</span>
                <span className="font-num text-white">{fmt(capUsed)} / {fmt(cap)}</span>
              </div>
              <div className="h-3 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all" style={{ width: `${pct}%` }} />
              </div>
              {pct >= 90 && <p className="text-xs text-amber-400 mt-2">You are near your earning cap — re-top-up to keep earning.</p>}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400 mb-4">No active package. Activate one to start earning.</p>
              <Link to="/app/packages" className="text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-slate-900 px-5 py-2.5 rounded-xl">Choose a Package</Link>
            </div>
          )}
        </div>

        {/* Magic cards + referral quick */}
        <div className="space-y-6">
          <div className="glass rounded-2xl p-6" data-testid="magic-summary">
            <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4"><Sparkles className="h-5 w-5 text-violet-400" /> Magic Cards</h3>
            <div className="flex items-end justify-between">
              <div>
                <div className="font-num text-3xl font-bold text-white">{data.usable_magic_cards}</div>
                <div className="text-xs text-slate-500">usable now</div>
              </div>
              <div className="text-right text-xs text-slate-400">
                <div>Total: <span className="text-white font-num">{u.magic_cards_total}</span></div>
              </div>
            </div>
            <div className="mt-4 rounded-xl bg-white/5 border border-white/10 p-3 flex items-center justify-between gap-3">
              <div>
                <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">Magic Card Wallet</div>
                <div className="font-num text-lg font-bold text-violet-300" data-testid="dashboard-magic-wallet">{fmt(u.magic_card_wallet)}</div>
              </div>
              <button data-testid="dashboard-buy-card-btn" disabled={buyingCards || Math.floor(u.magic_card_wallet || 0) < 1} onClick={buyCards}
                className="shrink-0 inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-400 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 rounded-xl">
                {buyingCards ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Buy
              </button>
            </div>
            <p className="text-[0.7rem] text-slate-500 mt-2">1 Magic Card = $1. Buy converts your whole-dollar balance into cards.</p>
            <Link to="/app/cards" className="mt-4 block text-center text-sm font-medium border border-white/10 hover:border-violet-500/40 rounded-xl py-2 text-violet-300">Open Magic Cards</Link>
          </div>
        </div>
      </div>

      {/* Level income */}
      <div className="glass rounded-2xl p-6 mb-8" data-testid="level-income-card">
        <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-5"><Layers className="h-5 w-5 text-cyan-400" /> Level Income</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {[1, 2, 3, 4, 5, 6].map((l) => (
            <div key={l} className="rounded-xl bg-white/5 border border-white/10 p-4">
              <div className="text-[0.65rem] uppercase tracking-wide text-slate-500">Level {l}</div>
              <div className="font-num text-lg font-bold text-white mt-1">{fmt(li[`level_${l}`])}</div>
            </div>
          ))}
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4">
            <div className="text-[0.65rem] uppercase tracking-wide text-emerald-400">Total</div>
            <div className="font-num text-lg font-bold text-emerald-300 mt-1">{fmt(li.total)}</div>
          </div>
        </div>
      </div>

      {/* Referral overview + recent activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="glass rounded-2xl p-6" data-testid="referral-overview">
          <h3 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-4"><Users className="h-5 w-5 text-emerald-400" /> Referrals</h3>
          <div className="space-y-3">
            <Row label="Direct referrals" value={data.direct_count} />
            <Row label="Active directs" value={data.active_direct_count} />
            <Row label="Your code" value={<span className="font-num text-emerald-300">{u.referral_code}</span>} />
          </div>
          <Link to="/app/referrals" className="mt-4 block text-center text-sm font-medium border border-white/10 hover:border-emerald-500/40 rounded-xl py-2 text-emerald-300">View referral dashboard</Link>
        </div>

        <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="recent-activity">
          <h3 className="font-display text-lg font-semibold text-white mb-4">Recent Activity</h3>
          {data.recent_transactions.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">No transactions yet.</p>
          ) : (
            <div className="space-y-1">
              {data.recent_transactions.map((t) => (
                <div key={t.id} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                  <div>
                    <div className="text-sm text-white capitalize">{t.type.replace(/_/g, " ")}</div>
                    <div className="text-xs text-slate-500 font-num">{new Date(t.created_at).toLocaleString()}</div>
                    {t.from_code && <div className="text-[0.65rem] font-num text-cyan-300">ID: {t.from_code}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-num text-sm text-white">{fmt(t.net)}</div>
                    <StatusPill status={t.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div><div className="text-[0.65rem] uppercase tracking-wide text-slate-500">{label}</div><div className="font-num text-base font-semibold text-white mt-0.5">{value}</div></div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm"><span className="text-slate-400">{label}</span><span className="font-num text-white">{value}</span></div>
);
